import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Gavel, MessageCircle, Gift } from "lucide-react";
import GoogleLoginButton from "../components/GoogleLoginButton";


export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "", referral_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto-isi kode referal dari link ?ref=KODE
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.trim().toUpperCase() }));
  }, [searchParams]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, referral_code: form.referral_code?.trim() || undefined, role: "buyer" });
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Registration failed");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div data-testid="register-page" className="max-w-md mx-auto px-4 md:px-8 py-16">
      <span className="tag tag-accent">// JOIN USTORE</span>
      <h1 className="font-display font-black text-4xl tracking-tighter text-white mt-3">Create account</h1>
      <p className="text-white/60 text-sm mt-2 mb-6">Buat akun Buyer untuk mulai belanja & ikut auction.</p>

      {/* Become Seller CTA banner */}
      <Link
        to="/become-seller"
        data-testid="become-seller-banner"
        className="block border border-[#FFD700]/40 hover:border-[#FFD700] bg-[#FFD700]/5 hover:bg-[#FFD700]/10 p-3 mb-6 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center shrink-0">
            <Gavel className="w-4 h-4 text-[#FFD700]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[#FFD700] text-sm">Mau jadi Seller?</p>
            <p className="text-white/60 text-[11px] mt-0.5">Verifikasi via WhatsApp admin — fee 3%, fitur lelang, auto-payout.</p>
          </div>
          <MessageCircle className="w-4 h-4 text-[#FFD700]" />
        </div>
      </Link>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">Full name</label>
          <input
            data-testid="register-name"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white p-3 text-sm mt-2"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">Email</label>
          <input
            data-testid="register-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white p-3 text-sm mt-2"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">Password</label>
          <input
            data-testid="register-password"
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white p-3 text-sm mt-2"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5 text-[#00F0FF]" /> Kode Referal <span className="text-white/30 normal-case tracking-normal">(opsional)</span>
          </label>
          <input
            data-testid="register-referral"
            value={form.referral_code}
            onChange={(e) => update("referral_code", e.target.value.toUpperCase())}
            placeholder="Punya kode dari teman? Masukkan di sini"
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white p-3 text-sm mt-2 uppercase tracking-wider placeholder:normal-case placeholder:tracking-normal placeholder:text-white/30"
          />
          <p className="text-white/40 text-[11px] mt-1.5">Dapatkan bonus untuk temanmu saat kamu belanja pertama kali.</p>
        </div>
        {error && <p data-testid="register-error" className="text-[#FF003C] text-sm">{error}</p>}

        <button data-testid="register-submit-btn" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
          {loading ? "Creating..." : "Create Buyer Account"}
        </button>
      </form>
      <GoogleLoginButton onError={setError} redirectTo="/" />
      <p className="text-white/50 text-sm mt-6 text-center">
        Have an account? <Link to="/login" className="text-[#00F0FF] hover:underline">Sign in</Link>
      </p>

    </div>
  );
}
